import { Dispatch } from '@reduxjs/toolkit';
import {
  CHECK_LOGIN,
  CHECK_LOGIN_STATUS,
  setAgentBusinessVertical,
  setAuthData,
  setCallingWindow,
  setCaptchaVerified,
  setCaptchaVisibility,
  setLonghornFirebaseToken,
  setReloadCaptcha,
  setServerDate
} from '../../reducers/commonSlice';
import {
  setCallbridgeData,
  setLeaderboard,
  setMonthlyPerformance,
  setPercentCompletedCases,
  setPromiseCount,
  setWeeklyPerformance,
  showLoader
} from '../../reducers/leaderboardSlice';
import axiosInstance, {
  ApiKeys,
  getApiUrl,
  getErrorMessage,
  logError,
  navigate
} from '../../utils/ApiHelper';
import {
  resetGlobalError,
  setFormLoading,
  setGlobalError,
  setShowOTPScreen
} from './reducers/authSlice';

import crypto from 'crypto-js';
import { getDeviceId } from 'src/utils/FingerPrintJS';
import { v4 as uuidv4 } from 'uuid';
import { removeAmeyoState } from '../../service/ameyo.service';
import { PERFORMANCE_CHART_TYPE } from 'src/components/leaderboard/leaderboardConstant';
import { toast } from '@navi/web-ui/lib/primitives/Toast';
import { LocalStorage, pushToLocalStorage } from 'src/utils/StorageUtils';
import { firebaseAuth } from '../../firebase/firebase';
import { LOCAL_STORAGE_KEYS } from '../../constants/StorageKeys';
import {
  GLOBAL,
  POST_MESSAGE_IDENTIFIER,
  setLonghornSessionVerifier
} from '@cp/src/constants/Global';
import { GetAgentCallAttemptedCountRequestParams } from '@cp/components/Ameyo/interfaces';
import {
  IMsClarityData,
  LONGHORN_SESSION_TOKEN,
  SendOTPPayload,
  CODE_VERIFIER,
  VerifyOTPPayload,
  Roles
} from './constants/AuthConstants';
import { setAgencyRankingData } from '@cp/src/reducers/AgencyDetailsSlice';
import logger from '@cp/src/utils/logger';
import APP_ROUTES, { LOGIN_PATH } from '@cp/src/layout/Routes';
import { IT_TEAM_ROLES_COUNT, requiredRolesForItTeam } from '@cp/src/constants/Common.constants';
import { API_STATUS_CODE } from '@cp/src/utils/ApiHelperUtils';
import store from '@cp/src/store';

const defaultGuestUserMsClarityData: IMsClarityData = {
  referenceId: 'guestUser',
  phoneNumber: '',
  email: ''
};

const setMsClarityData = (msClarityData = defaultGuestUserMsClarityData) => {
  if (window.clarity && typeof window.clarity === 'function') {
    window.clarity('identify', msClarityData?.referenceId);
    window.clarity('set', 'agentPhone', msClarityData?.phoneNumber);
    window.clarity('set', 'agentEmail', msClarityData?.email?.toLowerCase());
  }
};

const getWAExtensionId = localStorage?.getItem('WA_EXTENSION_ID');

export const sendMessageToPlugin = async (params: any) => {
  if (typeof chrome !== 'undefined' && typeof chrome?.runtime !== 'undefined') {
    if (getWAExtensionId) {
      await chrome.runtime.sendMessage(getWAExtensionId || '', {
        authParams: params
      });
    }
  }
};

export const sendWhatsappCredentials = async (params: any) => {
  window.postMessage({
    type: POST_MESSAGE_IDENTIFIER.SEND_WHATSAPP_CREDENTIALS,
    data: params
  });
};

export const getAuthToken = () => localStorage.getItem(LONGHORN_SESSION_TOKEN);
export const clearToken = () => {
  setMsClarityData();
  LocalStorage.deleteItem(LONGHORN_SESSION_TOKEN);
  LocalStorage.deleteItem(LOCAL_STORAGE_KEYS.LONGHORN_SESSION_IDENTIFIER);
  LocalStorage.deleteItem(LOCAL_STORAGE_KEYS.LONGHORN_FIREBASE_TOKEN);
  LocalStorage.deleteItem(LOCAL_STORAGE_KEYS.IS_FIREBASE_SIGNED_IN);
  sendMessageToPlugin(null);
  sendWhatsappCredentials(null);
  removeAmeyoState();
  LocalStorage.clearStorage();
  sessionStorage.clear();
};

export const sendOTP = ({ phoneNumber }: SendOTPPayload) => {
  const url = getApiUrl(ApiKeys.SEND_OTP);
  const data = { phoneNumber };
  return async function (dispatch: (arg0: { payload: any; type: string }) => void) {
    dispatch(setFormLoading(true));
    await axiosInstance
      .post(url, data, { headers: { donotHandleError: true, includeInUptimeMetrics: true } })
      .then(response => {
        if (response.status === 200) {
          if (response?.data?.otpToken) {
            dispatch(
              setShowOTPScreen({
                status: true,
                phoneNumber,
                otpToken: response?.data?.otpToken
              })
            );
            dispatch(setCaptchaVisibility(response?.data?.showCaptcha));
          }
        }
      })
      .catch(error => {
        const message = getErrorMessage(error);
        dispatch(setGlobalError({ message }));
      });
  };
};

export const signInWithGoogle = () => {
  const state = uuidv4();
  const codeVerifier = crypto?.lib?.WordArray?.random(64)?.toString();
  const codeChallenge = crypto?.enc?.Base64url?.stringify(
    crypto?.enc?.Utf8?.parse(crypto?.SHA256(codeVerifier))
  )?.toString();
  const url = getApiUrl(ApiKeys.OAUTH_SIGNIN) + `?state=${state}&codeChallenge=${codeChallenge}`;

  localStorage?.setItem(CODE_VERIFIER, codeVerifier);

  axiosInstance
    .get(url)
    .then(response => {
      const signInUrl = response?.data?.signInUrl;
      window?.location?.replace(signInUrl);
    })
    .catch(() => {
      clearToken();
    });
};

export const exchangeSession =
  (code: string, state: string, fcmToken: string) => (dispatch: Dispatch) => {
    const url = getApiUrl(ApiKeys.OAUTH_EXCHANGE_SESSION);
    const codeVerifier = localStorage.getItem(CODE_VERIFIER);

    const data = {
      code: code,
      codeVerifier: codeVerifier,
      state: state,
      deviceId: getDeviceId(),
      fcmToken
    };
    dispatch(resetGlobalError());
    axiosInstance
      .post(url, data, { headers: { donotHandleError: true, includeInUptimeMetrics: true } })
      .then(response => {
        if (response.status === 200) {
          if (response?.data?.accessToken && response?.data?.accessTokenIdentifier) {
            localStorage.setItem(LONGHORN_SESSION_TOKEN, response?.data?.accessToken);
            localStorage.setItem(CHECK_LOGIN, CHECK_LOGIN_STATUS.TRUE);
            LocalStorage.setItem(
              LOCAL_STORAGE_KEYS.LONGHORN_SESSION_IDENTIFIER,
              btoa(response.data.accessTokenIdentifier)
            );
            setLonghornSessionVerifier(response?.data?.accessTokenIdentifier);
            dispatch(
              setAuthData({
                token: response.data.accessToken,
                isLoggedIn: true,
                longhornFirebaseToken: response.data.longhornFirebaseToken
              })
            );
            pushToLocalStorage(
              LOCAL_STORAGE_KEYS.LONGHORN_FIREBASE_TOKEN,
              response.data.longhornFirebaseToken
            );
            dispatch(setFormLoading(false));
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            dispatch(verifyUserSession());
            const authParams = {
              sessionId: response.data.accessToken,
              deviceId: getDeviceId()
            };
            sendMessageToPlugin(authParams);
            sendWhatsappCredentials({
              sessionId: response.data.accessToken,
              deviceId: getDeviceId()
            });
          }
        }
      })
      .catch(() => {
        const message = 'Google login unauthorized.';
        dispatch(setGlobalError({ message }));
      });
  };

export const getEncryptedSlashCrmId = () => (dispatch: Dispatch) => {
  const url = getApiUrl(ApiKeys.FETCH_SLASH_CRM_ID);
  axiosInstance.get(url, { headers: { includeInUptimeMetrics: true } }).then(response => {
    dispatch(setAuthData(response?.data));
  });
};

export const fetchCurrentDate = () => (dispatch: Dispatch) => {
  const url = getApiUrl(ApiKeys.GET_SERVER_TIME);
  axiosInstance
    .get(url, { headers: { includeInUptimeMetrics: true } })
    .then(res => {
      dispatch(setServerDate(res?.data?.currentTimestamp));
    })
    .catch(() => {
      toast('Failed to fetch server time', { type: 'error' });
    });
};

export const verifyUserSession = () => {
  const ALLOWED_ROUTES_FOR_SELF_NAVIGATION = [APP_ROUTES.IMPERSONATION.path, LOGIN_PATH];
  const url = getApiUrl(ApiKeys.VERIFY_USER_SESSION);
  return async function (dispatch: (arg0: { payload: any; type: string }) => void) {
    await axiosInstance
      .get(url, { headers: { donotHandleError: true, includeInUptimeMetrics: true } })
      .then(response => {
        const agentBusinessVertical = response?.data?.businessVerticals?.[0] || '';
        GLOBAL.AGENT_BUSINESS_VERTICAL = agentBusinessVertical;
        dispatch(setAgentBusinessVertical(agentBusinessVertical));
        dispatch(setAuthData(response?.data));
        const isSelfRedirectionAllowed = ALLOWED_ROUTES_FOR_SELF_NAVIGATION.some(route =>
          window.location.pathname.includes(route)
        );
        if (isSelfRedirectionAllowed) {
          if (
            response.data.roles?.length === IT_TEAM_ROLES_COUNT &&
            requiredRolesForItTeam.every(role => response.data.roles?.includes(role))
          ) {
            navigate(APP_ROUTES.ALL_AGENTS.path);
          } else if (response.data.roles?.includes(Roles.ROLE_TEAM_LEAD)) {
            navigate(APP_ROUTES.TEAM_LEAD_DASHBOARD.path);
          } else {
            navigate(APP_ROUTES.CASES.path);
          }
        }
        if (response?.data) {
          setMsClarityData({
            referenceId: response.data.referenceId,
            phoneNumber: response.data.phoneNumber,
            email: response.data.email
          });
          removeAmeyoState();
        }
      })
      .catch(() => {
        dispatch(setAuthData({ token: null }));
        clearToken();
      });
  };
};

export const getCallingWindow = () => {
  const url = getApiUrl(ApiKeys.GET_CALLING_WINDOW);
  return async function (dispatch: (arg0: { payload: any; type: string }) => void) {
    await axiosInstance
      .get(url, { headers: { donotHandleError: true, includeInUptimeMetrics: true } })
      .then(response => {
        dispatch(setCallingWindow(response?.data));
      })
      .catch(() => {
        logError('Error while fetching calling window');
      });
  };
};

export const verifyOTP = (
  { otp, otpToken, source, deviceId, phoneNumber, captchaVerified, fcmToken }: VerifyOTPPayload,
  navigateToCases: () => void
) => {
  const url = getApiUrl(ApiKeys.VERIFY_OTP);
  const data = { otp, otpToken, source, deviceId, captchaVerified, fcmToken };
  return async function (dispatch: (arg0: { payload: any; type: string }) => void) {
    dispatch(setFormLoading(true));
    dispatch(resetGlobalError());
    await axiosInstance
      .post(url, data, { headers: { donotHandleError: true, includeInUptimeMetrics: true } })
      .then(response => {
        if (response?.data?.accessToken && response?.data?.accessTokenIdentifier) {
          localStorage.setItem('LONGHORN_SESSION_TOKEN', response.data.accessToken);
          localStorage.setItem('pno', btoa(phoneNumber || ''));
          LocalStorage.setItem(
            LOCAL_STORAGE_KEYS.LONGHORN_SESSION_IDENTIFIER,
            btoa(response?.data?.accessTokenIdentifier)
          );

          setLonghornSessionVerifier(response?.data?.accessTokenIdentifier);
          dispatch(
            setAuthData({
              token: response.data.accessToken,
              isLoggedIn: true,
              longhornFirebaseToken: response.data.longhornFirebaseToken
            })
          );
          pushToLocalStorage(
            LOCAL_STORAGE_KEYS.LONGHORN_FIREBASE_TOKEN,
            response.data.longhornFirebaseToken
          );
          navigateToCases();
          const authParams = {
            sessionId: response.data.accessToken,
            deviceId: getDeviceId()
          };
          sendMessageToPlugin(authParams);
          sendWhatsappCredentials({
            sessionId: response.data.accessToken,
            deviceId: getDeviceId()
          });
        }
      })
      .catch(error => {
        dispatch(setCaptchaVisibility(error?.response?.data?.data?.showCaptcha));
        const message = error?.response?.data?.message;
        dispatch(setGlobalError({ message }));
        dispatch(setReloadCaptcha(true));
        dispatch(setCaptchaVerified(false));
      })
      .finally(() => {
        dispatch(setFormLoading(false));
        dispatch(setReloadCaptcha(false));
      });
  };
};

const cleanUpToken = () => {
  clearToken();
  window?.location?.reload();
};

export const logOut = () => {
  if (!getAuthToken() || !getDeviceId()) {
    cleanUpToken();
  } else {
    const url = getApiUrl(ApiKeys.LOGOUT);
    axiosInstance
      .post(url, {}, { headers: { includeInUptimeMetrics: true } })
      .then(() => cleanUpToken())
      .catch(err => {
        if (err?.response?.status === API_STATUS_CODE.FORBIDDEN) {
          cleanUpToken();
        }
      });
  }
};

export const getLeaderboardDetails = (
  agentReferenceId: string,
  month: string,
  filter = '',
  year: number
) => {
  const params = encodeURIComponent(filter);
  const filterParam = `?filter=${params}`;
  const url = getApiUrl(ApiKeys.GET_LEADERBOARD_DETAILS, { agentReferenceId }) + filterParam;
  return async function (dispatch: (arg0: { payload: any; type: string }) => void) {
    dispatch(showLoader(true));
    await axiosInstance
      .get(url, {
        headers: { donotHandleError: true, includeInUptimeMetrics: true },
        params: { cashCollectedBreakupMonth: month, cashCollectedBreakupYear: year }
      })
      .then(response => {
        dispatch(setLeaderboard(response?.data));
      })
      .catch(err => {
        dispatch(showLoader(false));
        logError(err, 'Getting error from getLeaderboardDetails :: ' + url);
      });
  };
};

export const getAgencyRanking = (agencyCode: string) => (dispatch: Dispatch) => {
  const url = getApiUrl(ApiKeys.GET_AGENCY_RANKING, { agencyCode });
  axiosInstance
    .get(url, { headers: { includeInUptimeMetrics: true } })
    .then(response => {
      dispatch(setAgencyRankingData(response?.data));
    })
    .catch(error => {
      logError('Error inside getAgencyRanking API', error);
    });
};

export const getPerformance = (agentReferenceId: string, chartType: string) => {
  const url = getApiUrl(ApiKeys.GET_PERFORMANCE, { agentReferenceId, chartType });
  return async function (dispatch: (arg0: { payload: any; type: string }) => void) {
    axiosInstance
      .get(url, { headers: { donotHandleError: true, includeInUptimeMetrics: true } })
      .then(response => {
        chartType === PERFORMANCE_CHART_TYPE.LAST_30_DAYS
          ? dispatch(setMonthlyPerformance(response?.data))
          : dispatch(setWeeklyPerformance(response?.data));
      })
      .catch(err => {
        logError(err, 'Getting error from getPerformance :: ' + url);
      });
  };
};

export const verifyCaptcha = async (response: string, phoneNumber: any) => {
  const url = getApiUrl(ApiKeys.VERIFY_CAPTCHA);
  try {
    const verifyCaptchaResponse = await axiosInstance.post(
      url,
      {},
      {
        params: {
          response: response,
          phoneNumber: phoneNumber
        },
        headers: { includeInUptimeMetrics: true }
      }
    );
    const data = verifyCaptchaResponse?.data;
    return data?.success;
  } catch {
    clearToken();
  }
};

export const getPercentCompleted = () => {
  const url = getApiUrl(ApiKeys.DASHBOARD_INTERACTIONS_STATUS);
  return async function (dispatch: (arg0: { payload: any; type: string }) => void) {
    await axiosInstance
      .get(url, { headers: { donotHandleError: true, includeInUptimeMetrics: true } })
      .then(response => {
        dispatch(setPercentCompletedCases(response?.data));
      })
      .catch(err => {
        logError(err, 'Getting error from  dashboard/interaction-status api:: ' + url);
      });
  };
};

export const getPromiseCount = () => {
  const url = getApiUrl(ApiKeys.DASHBOARD_PROMISE_COUNT);
  return async function (dispatch: (arg0: { payload: any; type: string }) => void) {
    await axiosInstance
      .get(url, { headers: { donotHandleError: true } })
      .then(response => {
        dispatch(setPromiseCount(response?.data));
      })
      .catch(err => {
        logError(err, 'Getting error from dashboard/promise-count api:: ' + url);
      });
  };
};

export const getCallBridgeData = (params: GetAgentCallAttemptedCountRequestParams) => {
  const url = getApiUrl(ApiKeys.DASHBOARD_CALLBRIDGE_DATA);
  return async function (dispatch: (arg0: { payload: any; type: string }) => void) {
    await axiosInstance
      .get(url, {
        headers: { donotHandleError: true },
        params
      })
      .then(response => {
        dispatch(setCallbridgeData(response?.data));
      })
      .catch(err => {
        logError(err, 'Getting error from dashboard/call-attempted-count api:: ' + url);
      });
  };
};

export const signInToFirebase = async (
  longhornFirebaseToken: string,
  successCallback: () => void
) => {
  if (localStorage.getItem(LOCAL_STORAGE_KEYS.IS_FIREBASE_SIGNED_IN) === 'true') {
    return;
  }
  const token =
    longhornFirebaseToken || LocalStorage.getItem(LOCAL_STORAGE_KEYS.LONGHORN_FIREBASE_TOKEN);
  firebaseAuth()
    .signInWithCustomToken(token)
    .then(() => {
      successCallback();
    })
    .catch(() => {
      toast('Error in signInUserToFirebase', {
        type: 'error'
      });
    });
};

export const registerFCMToken = (fcmToken: string) => {
  axiosInstance.put(
    getApiUrl(ApiKeys.REGISTER_FCM_TOKEN),
    { fcmToken },
    { headers: { donotHandleError: true, includeInUptimeMetrics: true } }
  );
};

export const refreshFirebaseTokenAndSignIn = () => (dispatch: Dispatch) => {
  firebaseAuth()
    .currentUser?.getIdToken(true)
    ?.then(token => {
      dispatch(setLonghornFirebaseToken(token));
    })
    .catch(err => {
      logger('Error while signing in to firebase');
      logError(err, 'Error while refreshing firebase token');
    });
};
